import { Wifi, WifiOff, RefreshCw, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useSyncStatus } from '@/hooks/useSyncStatus';
import { useSync } from '@/contexts/SyncContext';

interface Props {
  collapsed?: boolean;
}

export function SyncStatusIndicator({ collapsed = false }: Props) {
  const { isOnline, isSyncing, pendingCount, failedCount } = useSyncStatus();
  const { triggerSync } = useSync();

  // Determine state
  let icon: React.ReactNode;
  let label: string;
  let colorClass: string;

  if (failedCount > 0) {
    icon = <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />;
    label = `${failedCount} failed`;
    colorClass = 'text-[hsl(var(--status-red))]';
  } else if (isSyncing) {
    icon = <RefreshCw className="h-3.5 w-3.5 flex-shrink-0 animate-spin" />;
    label = 'Syncing…';
    colorClass = 'text-muted-foreground';
  } else if (!isOnline && pendingCount > 0) {
    icon = <WifiOff className="h-3.5 w-3.5 flex-shrink-0" />;
    label = `${pendingCount} queued`;
    colorClass = 'text-[hsl(var(--status-amber))]';
  } else if (!isOnline) {
    icon = <WifiOff className="h-3.5 w-3.5 flex-shrink-0" />;
    label = 'Offline';
    colorClass = 'text-muted-foreground/60';
  } else if (pendingCount > 0) {
    icon = <Wifi className="h-3.5 w-3.5 flex-shrink-0" />;
    label = `${pendingCount} pending`;
    colorClass = 'text-[hsl(var(--status-amber))]';
  } else {
    icon = <Wifi className="h-3.5 w-3.5 flex-shrink-0" />;
    label = 'Synced';
    colorClass = 'text-[hsl(var(--status-green)/0.7)]';
  }

  const handleRetry = (e: React.MouseEvent) => {
    e.preventDefault();
    triggerSync();
  };

  if (collapsed) {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <button
            onClick={failedCount > 0 ? handleRetry : undefined}
            className={cn('w-full flex items-center justify-center py-2', colorClass, failedCount > 0 && 'cursor-pointer hover:opacity-80')}
          >
            {icon}
          </button>
        </TooltipTrigger>
        <TooltipContent side="right" className="text-xs">
          {label}{failedCount > 0 ? ' · Click to retry' : ''}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <div className={cn('flex items-center gap-2 px-3 py-2 text-[11px]', colorClass)}>
      {icon}
      <span className="flex-1 truncate">{label}</span>
      {failedCount > 0 && (
        <button
          onClick={handleRetry}
          className="text-[10px] underline underline-offset-2 hover:opacity-80 flex-shrink-0"
        >
          Retry
        </button>
      )}
    </div>
  );
}
